import React, { useState, useEffect, useCallback } from 'react';
import '../../styles/PageCommon.css';
import './DashboardPage.css';
import SegmentedControl from '../../components/common/SegmentedControl/SegmentedControl';
import { ReactComponent as CalendarIcon } from '../../assets/icons/calendar-icon.svg';
import { ReactComponent as ArrowDownIcon } from '../../assets/icons/arrow-down-icon.svg';
import { ReactComponent as SalesIcon } from '../../assets/icons/sales-icon.svg';
import CalendarModal from '../../components/common/CalendarModal/CalendarModal';
import DemandPlanningReport from '../../components/DemandPlanningReport/DemandPlanningReport';
import { dashboardService } from '../../services/dashboardService';
import { parseApiError } from '../../utils/parseErrorMessage';
import LoadingAnimation from '../../components/common/LoadingAnimation/LoadingAnimation';
import { formatInputDateMonth, formatToReadableDate, mergePlotDataDashboard, numberToKiloFormat } from '../../utils/utils';
import { NotificationContainer } from '../../components/common/NotificationContainer';
import NotificationModal from '../../components/common/NotificationModal';
import { useFilterParams } from '../../hooks/useFilterParams';

function fulfillmentClass(pct) {
  return pct >= 100 ? 'healthy' : pct >= 80 ? 'normal' : 'critical';
}

const DEFAULT_FILTERS = {
  date_from: formatInputDateMonth(new Date(new Date().setMonth(new Date().getMonth() - 6))),
  date_to: null,//formatIntoIsoDate(new Date(new Date().setMonth(new Date().getMonth() + 6))),
  view_type: "dsp",
};

export default function DashboardPage() {
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [isCalendarFilterOpen, setIsCalendarFilterOpen] = useState(false);
  const [overviewData, setOverviewData] = useState({});
  const [categoryA, setCategoryA ] = useState({})
  const [categoryB, setCategoryB ] = useState({})
  const [categoryC, setCategoryC ] = useState({})
  const [plotData, setPlotData] = useState([]);
  const [planningMonth, setPlanningMonth] = useState(null);

  const [bestBranches, setBestBranches] = useState([]);
  const [worstBranches, setWorstBranches] = useState([]);
  const [topSku, setTopSku] = useState([]);

  const [dataError, setDataError] = useState(null);

  // --- Filters State and Logic --- 
  const { filters, updateFilters } = useFilterParams(DEFAULT_FILTERS);

  const fetchDashboardData = useCallback(async () => {
    setIsLoadingData(true);
    const onError = (e) => setDataError(parseApiError(e) || "");

    await Promise.allSettled([
      dashboardService.getOverview(filters.view_type, filters.date_from, filters.date_to)
        .then(r => r && setOverviewData(r)).catch(onError),
      dashboardService.getCategoryA(filters.view_type, filters.date_from, filters.date_to)
        .then(r => r && setCategoryA(r)).catch(onError),
      dashboardService.getCategoryB(filters.view_type, filters.date_from, filters.date_to)
        .then(r => r && setCategoryB(r)).catch(onError),
      dashboardService.getCategoryC(filters.view_type, filters.date_from, filters.date_to)
        .then(r => r && setCategoryC(r)).catch(onError),
      dashboardService.getSkuSalesOverview(filters.view_type, filters.date_from, filters.date_to)
        .then(r => r?.top_skus && setTopSku(r.top_skus)).catch(onError),
      dashboardService.getBranchSalesOverview(filters.view_type, filters.date_from, filters.date_to)
        .then(r => {
          if (r?.top_branches) setBestBranches(r.top_branches);
          if (r?.bottom_branches) setWorstBranches(r.bottom_branches);
        }).catch(onError),
      dashboardService.plotData(filters.view_type, filters.date_from, filters.date_to)
        .then(r => {
          if (r?.aggregated_historical_sales_data || r?.aggregated_forecast_sales_data) {
            console.log("r?.aggregated_historical_sales_data: ", r);
            setPlotData(mergePlotDataDashboard(r.aggregated_historical_sales_data, r.aggregated_forecast_sales_data));
            setPlanningMonth(r.planning_month);
          }
        }).catch(onError),
    ]);

    setIsLoadingData(false);
  }, [filters]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);


  const sortedCategories = [
    categoryA?.sales_share_percent ?? 0,
    categoryB?.sales_share_percent ?? 0,
    categoryC?.sales_share_percent ?? 0,
  ];

  function assignColors(values) {
    const max = Math.max(...values);
    const min = Math.min(...values);

    return values.map(v => {
      if (v === max) return "green";
      if (v === min) return "red";
      return "orange";
    });
  }
  // eslint-disable-next-line no-unused-vars
  const [colorA, colorB, colorC] = assignColors(sortedCategories);

  return (
    <>
      {isLoadingData && <LoadingAnimation />}
      <div className="page-content">
        <NotificationContainer>
          {
            dataError && <NotificationModal closeModal={() => setDataError(null)} type="error" subTitle={dataError} title="Ошибка" duration={4000}/>
          }
        </NotificationContainer>
        <div className="dashboard-header-container">
            <h1 className="dashboard-title">Общий обзор</h1>
            <div className='dashboard-header-buttons'>
              <SegmentedControl
                value={filters.view_type?.toLowerCase()}
                onChange={(value) => updateFilters({ view_type: value })}
                options={[
                {
                  label: "Деньги",
                  options: [
                    { label: "Прайс-лист", value: "dsp" },
                    { label: "Инвойс цена", value: "invoice price" },
                  ],
                },
                  { label: "Короб", value: "cases" },
                ]}
              />

              <button
                onClick={() => {console.log("isCalendarFilterOpen ", isCalendarFilterOpen);setIsCalendarFilterOpen(true)}}
                className="filter-select"
              >              
                <CalendarIcon size={16} color='#7CC805'/>
                <span className="filter-select-label">{formatToReadableDate(filters.date_from)} - {formatToReadableDate(filters.date_to)}</span>
                <ArrowDownIcon size={24} color='#7CC805'/>
              </button>
              {
                isCalendarFilterOpen && 
                  <CalendarModal 
                    title={"Выберите период"}
                    dateRange={{
                      startDate: filters.date_from,
                      endDate: filters.date_to
                    }} 
                    isOpen={isCalendarFilterOpen} 
                    closeModal={()=>{setIsCalendarFilterOpen(false)}} 
                    applyDateRangeFilter={(dateRange) => { 
                      updateFilters({ date_from: dateRange.startDate, date_to: dateRange.endDate }); 
                    }}
                    allowSameMonth={true}
                    />
              }
            </div>
        </div>

        <div className="dashboard-cards">
          <div className="dashboard-cards-row">
            <div className="sales-container" >
              <div className="dashboard-card-container">
                <div className="dashboard-card-container-header">
                  <span className="dashboard-card-container-header-title">
                    <SalesIcon/> Продажи
                  </span>
                </div>

                <div className="dashboard-cards-item-column">
                  <div className="dashboard-cards-item">
                    <span className="dashboard-cards-item-text">План</span>
                    <div className="dashboard-cards-item-value">
                      <h1 title={overviewData?.total_target_value?.toLocaleString("ru-RU")}>
                        {numberToKiloFormat(overviewData?.total_target_value)}  {filters.view_type === "DSP" && `₸`}
                      </h1>
                    </div>
                  </div>

                  <div className="dashboard-cards-item">
                    <span className="dashboard-cards-item-text">Факт</span>
                    <div className="dashboard-cards-item-value">
                      <h1 title={overviewData?.total_fact_value?.toLocaleString("ru-RU")}>
                        {numberToKiloFormat(overviewData?.total_fact_value)}  {filters.view_type === "DSP" && "₸"}
                      </h1>
                      <div className={`dashboard-cards-item-tendency ${overviewData?.trend_percent > 0 ? "green" : overviewData?.trend_percent < 0 ? "red" : 'neutral'}`}>
                        {overviewData?.trend_percent}%
                        {/* {
                          overviewData?.trend_percent !== 0 && 
                            <ArrowRightUp style={{transform: overviewData?.trend_percent < 0 && "rotate(90deg)"}}/>
                        } */}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="best-worst-branch-container" >
              <div className="dashboard-card-container" 
                style={{
                  flexDirection: "row", 
                  justifyContent: "space-between",
                  padding: "0px",
                  minWidth: "800px"
                }}
              >
                <div className="dashboard-top-table-left">
                  <span className="dashboard-card-container-header-title">
                    Лучшие {bestBranches?.length} складов
                  </span>
                  <table className="dashboard-table">
                    <thead>
                      <tr>
                        <th>
                          Склад
                        </th>
                        <th>
                          Продажи
                        </th>
                        <th>
                          Выполнение
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {bestBranches.map((row) => (
                        <tr key={row.branch_name}>
                          <td>{row.branch_name}</td>
                          <td>{row.fact_value_per_branch?.toLocaleString("ru-RU")}</td>
                          <td className={fulfillmentClass(row.sales_performance)}>{row.sales_performance}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="dashboard-top-right-container">
                  <span className="dashboard-card-container-header-title">
                    Худшие {worstBranches?.length} складов
                  </span>
                  <table className="dashboard-table">
                    <thead>
                      <tr>
                        <th>Склад</th>
                        <th>Продажи</th>
                        <th>Выполнение</th>
                      </tr>
                    </thead>
                    <tbody>
                      {worstBranches.map((row) => (
                        <tr key={row.branch_name}>
                          <td>{row.branch_name}</td>
                          <td>{row.fact_value_per_branch?.toLocaleString("ru-RU")}</td>
                          <td className={fulfillmentClass(row.sales_performance)}>{row.sales_performance}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>          
            </div>

          </div>
          <div className="dashboard-cards-row">
            <div className="top-sku-container dashboard-card-container">
                <span className="dashboard-card-container-header-title" style={{justifyContent: "center"}}>
                  ТОП-{topSku?.length} СКЮ
                </span>
                <div style={{
                  display: "flex",
                  flexDirection: "row", 
                  justifyContent: "space-between",
                  padding: "0px",
                  minWidth: "1000px"
                }}>
                  <div className="dashboard-top-table-left" style={{padding: "0px", paddingRight: "32px"}}>
                    <table className="dashboard-table">
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>СКЮ</th>
                          <th>Продажи</th>
                          <th>Выполнение</th>
                        </tr>
                      </thead>
                      <tbody>
                        {topSku.slice(0, 5).map((row, index) => (
                          <tr key={row.sku_name}>
                            <td>{index + 1}</td>
                            <td>{row.sku_name}</td>
                            <td>{row.fact_value_per_sku?.toLocaleString("ru-RU")}</td>
                            <td className={fulfillmentClass(row.sales_performance_per_sku)}>{row.sales_performance_per_sku}%</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {
                    topSku.length > 5 &&
                      <div className="dashboard-top-right-container" style={{padding: "0px", paddingLeft: "32px"}}>
                        <table className="dashboard-table">
                          <thead>
                            <tr>
                              <th>#</th>
                              <th>СКЮ</th>
                              <th>Продажи</th>
                              <th>Выполнение</th>
                            </tr>
                          </thead>
                          <tbody>
                            {topSku.slice(5).map((row, index) => (
                              <tr key={row.sku_name}>
                                <td>{index + 6}</td>
                                <td>{row.sku_name}</td>
                                <td>{row.fact_value_per_sku?.toLocaleString("ru-RU")}</td>
                                <td className={fulfillmentClass(row.sales_performance_per_sku)}>{row.sales_performance_per_sku}%</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                  }
                </div>
            </div>
          </div>
        </div>

        <DemandPlanningReport data={plotData} planningMonth={planningMonth} showPlanningPeriod={true}/>
      </div>
    </>
  );
}

